import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateMenuProduct,
  useMenuCollections,
  useMenuProducts,
  useMenuSections,
  useUpdateMenuProduct,
} from "@/hooks/use-menu";
import { useOrgCurrency } from "@/hooks/use-org-settings";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { UpsertMenuProductInput } from "@/types/menu";

const UPLOADTHING_BASE_URL = "https://paj514wbqe.ufs.sh/f";
const CENTS_PER_UNIT = 100;

const createEmptyFormState = (): UpsertMenuProductInput => ({
  name: "",
  description: "",
  price: 0,
  sectionId: null,
  collectionId: null,
  imageKey: null,
});

export default function ProductFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const currency = useOrgCurrency(activeOrganization?.id);
  const isZeroDecimal = currency === "UYU";
  const { data: productsData } = useMenuProducts(activeOrganization?.id);
  const { data: sectionsData } = useMenuSections(activeOrganization?.id);
  const { data: collectionsData } = useMenuCollections(activeOrganization?.id);
  const createProduct = useCreateMenuProduct();
  const updateProduct = useUpdateMenuProduct();
  const [formState, setFormState] = useState<UpsertMenuProductInput>(createEmptyFormState());
  const [priceDisplay, setPriceDisplay] = useState("");
  const [isRemovingImage, setIsRemovingImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const hydratedForIdRef = useRef<string | null>(null);

  const editingProduct = id ? productsData?.products.find((p) => p.id === id) : undefined;

  useEffect(() => {
    if (!editingProduct) return;
    if (hydratedForIdRef.current === editingProduct.id) return;

    setFormState({
      name: editingProduct.name,
      description: editingProduct.description ?? "",
      price: editingProduct.price,
      sectionId: editingProduct.sectionId,
      collectionId: editingProduct.collectionId,
      imageKey: editingProduct.imageKey,
    });
    const units = editingProduct.price / CENTS_PER_UNIT;
    setPriceDisplay(isZeroDecimal ? String(Math.round(units)) : units.toFixed(2));
    hydratedForIdRef.current = editingProduct.id;
  }, [editingProduct, isZeroDecimal]);

  const handleChange = <T extends keyof UpsertMenuProductInput>(
    field: T,
    value: UpsertMenuProductInput[T],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleRemoveImage = async () => {
    if (!formState.imageKey) return;

    setIsRemovingImage(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/upload`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageKey: formState.imageKey }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete image");
      }

      handleChange("imageKey", null);
      toast.success("Image removed");
    } catch {
      toast.error("Failed to remove image. Please try again.");
    } finally {
      setIsRemovingImage(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalized = priceDisplay.replace(",", ".").trim();
    const parsedUnits = Number(normalized);
    const priceCents =
      normalized === "" || Number.isNaN(parsedUnits) ? 0 : Math.round(parsedUnits * CENTS_PER_UNIT);

    const payload: UpsertMenuProductInput = {
      name: formState.name?.trim() ?? "",
      description: formState.description?.trim() || null,
      price: priceCents,
      sectionId: formState.sectionId || null,
      collectionId: formState.collectionId || null,
      imageKey: formState.imageKey?.trim() || null,
    };

    if (!payload.name) {
      toast.error(t("menu.products.notifications.nameRequired"));
      return;
    }

    if (id) {
      if (!editingProduct) {
        toast.error(
          t("menu.products.notifications.notFound", { defaultValue: "Product not found" }),
        );
        return;
      }

      const updatePayload: Partial<UpsertMenuProductInput> = {};
      if (payload.name !== editingProduct.name) {
        updatePayload.name = payload.name;
      }
      if (payload.description !== editingProduct.description) {
        updatePayload.description = payload.description;
      }
      if (payload.price !== editingProduct.price) {
        updatePayload.price = payload.price;
      }
      if (payload.sectionId !== editingProduct.sectionId) {
        updatePayload.sectionId = payload.sectionId;
      }
      if (payload.collectionId !== editingProduct.collectionId) {
        updatePayload.collectionId = payload.collectionId;
      }
      if (payload.imageKey !== editingProduct.imageKey) {
        updatePayload.imageKey = payload.imageKey;
      }

      if (Object.keys(updatePayload).length === 0) {
        toast.success(t("menu.products.notifications.updated"));
        navigate("/menu/products");
        return;
      }

      updateProduct.mutate(
        { data: updatePayload, id },
        {
          onError: (mutationError) => {
            toast.error(mutationError.message);
          },
          onSuccess: () => {
            toast.success(t("menu.products.notifications.updated"));
            navigate("/menu/products");
          },
        },
      );
      return;
    }

    createProduct.mutate(payload, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.products.notifications.created"));
        navigate("/menu/products");
      },
    });
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.products.title")}</CardTitle>
            <CardDescription>{t("menu.products.notifications.activeOrgRequired")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isSupermarket = activeOrganization.businessType === "supermarket";
  const copy = {
    description: isSupermarket
      ? t("menu.products.catalog.form.description", {
          defaultValue: "Agrega un producto al catálogo o edita uno existente.",
        })
      : t("menu.products.form.description"),
    namePlaceholder: isSupermarket
      ? t("menu.products.catalog.form.namePlaceholder", {
          defaultValue: "ej. Arroz, Manzanas, Detergente",
        })
      : t("menu.products.form.namePlaceholder"),
    noSection: isSupermarket
      ? t("menu.products.catalog.form.noSection", {
          defaultValue: "Sin categoría",
        })
      : t("menu.products.form.noSection"),
    section: isSupermarket
      ? t("menu.products.catalog.form.section", {
          defaultValue: "Categoría / subcategoría",
        })
      : t("menu.products.form.section"),
  };

  const formTitle = id ? t("menu.products.form.editTitle") : t("menu.products.form.createTitle");

  const submitLabel = id ? t("menu.products.form.save") : t("menu.products.form.create");

  const sectionOptions = (() => {
    if (!sectionsData?.sections) {
      return [];
    }

    const options: Array<{ id: string; label: string }> = [];
    const parentSections = sectionsData.sections.filter((section) => section.parentId === null);
    const childSections = sectionsData.sections.filter((section) => section.parentId !== null);

    for (const parentSection of parentSections) {
      options.push({ id: parentSection.id, label: parentSection.name });

      for (const childSection of childSections) {
        if (childSection.parentId === parentSection.id) {
          options.push({
            id: childSection.id,
            label: `${parentSection.name} / ${childSection.name}`,
          });
        }
      }
    }

    for (const childSection of childSections) {
      const hasParent = parentSections.some(
        (parentSection) => parentSection.id === childSection.parentId,
      );
      if (!hasParent) {
        options.push({ id: childSection.id, label: childSection.name });
      }
    }

    return options;
  })();

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">{formTitle}</h1>
        <p className="text-muted-foreground text-sm">{copy.description}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{formTitle}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="menu-product-name">{t("menu.products.form.name")}</Label>
              <Input
                id="menu-product-name"
                onChange={(event) => handleChange("name", event.target.value)}
                placeholder={copy.namePlaceholder}
                value={formState.name ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-product-description">
                {t("menu.products.form.longDescription")}
              </Label>
              <Textarea
                id="menu-product-description"
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder={t("menu.products.form.descriptionPlaceholder")}
                rows={4}
                value={formState.description ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-product-price">{t("menu.products.form.price")}</Label>
              <Input
                className="[appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                id="menu-product-price"
                min={0}
                onChange={(event) => setPriceDisplay(event.target.value)}
                placeholder={t("menu.products.form.pricePlaceholder")}
                step={isZeroDecimal ? 1 : 0.01}
                type="number"
                value={priceDisplay}
                inputMode="numeric"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-product-section">{copy.section}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                id="menu-product-section"
                onChange={(event) => handleChange("sectionId", event.target.value || null)}
                value={formState.sectionId ?? ""}
              >
                <option value="">{copy.noSection}</option>
                {sectionOptions.map((sectionOption) => (
                  <option key={sectionOption.id} value={sectionOption.id}>
                    {sectionOption.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-product-collection">{t("menu.products.form.collection")}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                id="menu-product-collection"
                onChange={(event) => handleChange("collectionId", event.target.value || null)}
                value={formState.collectionId ?? ""}
              >
                <option value="">{t("menu.products.form.noCollection")}</option>
                {collectionsData?.collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-product-image">{t("menu.products.form.imageKey")}</Label>
              {formState.imageKey ? (
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md border">
                  <img
                    alt={formState.name || "Product image"}
                    className="h-full w-full object-cover"
                    src={`${UPLOADTHING_BASE_URL}/${formState.imageKey}`}
                  />
                  <Button
                    className="absolute right-2 top-2 z-10"
                    onClick={handleRemoveImage}
                    disabled={isRemovingImage}
                    size="sm"
                    type="button"
                    variant="destructive"
                  >
                    {isRemovingImage ? "Removing..." : t("menu.products.form.removeImage")}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed p-8 text-center relative z-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <svg
                      className="h-6 w-6 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">PNG, JPG or WEBP (max. 4MB)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                    disabled={isUploadingImage}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      if (file.size > 4 * 1024 * 1024) {
                        toast.error("File too large. Maximum size is 4MB.");
                        return;
                      }

                      const previousImageKey = formState.imageKey;

                      const formData = new FormData();
                      formData.append("file", file);

                      setIsUploadingImage(true);
                      try {
                        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/upload`, {
                          method: "POST",
                          body: formData,
                          credentials: "include",
                        });

                        if (!response.ok) {
                          throw new Error("Upload failed");
                        }

                        const data = await response.json();

                        if (previousImageKey) {
                          fetch(`${import.meta.env.VITE_API_URL}/api/upload`, {
                            method: "DELETE",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ imageKey: previousImageKey }),
                            credentials: "include",
                          });
                        }

                        handleChange("imageKey", data.imageKey);
                        toast.success("Image uploaded successfully");
                      } catch {
                        toast.error("Upload failed. Please try again.");
                      } finally {
                        setIsUploadingImage(false);
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button disabled={createProduct.isPending || updateProduct.isPending} type="submit">
                {submitLabel}
              </Button>
              <Button onClick={() => navigate("/menu/products")} type="button" variant="outline">
                {t("menu.products.form.cancel")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
